import { apolloClient } from '@client/api/graphql/client';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  Author,
  GetCommunityPostDocument,
  GetCommunityPostQueryVariables,
  GetCommunityPostQuery,
  Reply,
} from '../../../../generated/graphql';
import { communityQueryKeys } from '../constants';
import { getParams } from '@client/new/community/hooks/useCommunityPosts';

const REPLIES_PAGINATION_SIZE = 25;

type AuthorWithOptionalMarketing = Omit<Author, 'marketing'> &
  Partial<Pick<Author, 'marketing'>>;

type ReplyWithOptionalMarketing = Omit<Reply, 'author'> & {
  author: AuthorWithOptionalMarketing;
};

export const useCommunityComments = (
  communityId: number,
  postId: string,
  lastId?: string,
) => {
  const withMarketing = (author: AuthorWithOptionalMarketing): Author => ({
    ...author,
    marketing: typeof author.marketing === 'boolean' ? author.marketing : null,
  });

  const mapReplies = (replies?: ReplyWithOptionalMarketing[] | null): Reply[] =>
    (replies || []).map(
      (reply) =>
        ({
          ...reply,
          author: withMarketing(reply.author),
        } as Reply),
    );

  return useInfiniteQuery({
    queryKey: communityQueryKeys.postComments(Number(communityId), postId),
    queryFn: async ({ pageParam = {} }) => {
      const response = await apolloClient.query<
        GetCommunityPostQuery,
        GetCommunityPostQueryVariables
      >({
        query: GetCommunityPostDocument,
        variables: {
          communityId,
          postId,
          lastId: lastId || undefined,
          ...getParams(pageParam),
        },
        fetchPolicy: 'network-only',
      });
      return mapReplies(response.data.post?.replies);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage && lastPage.length < REPLIES_PAGINATION_SIZE) {
        return;
      }
      const lastPostComment =
        lastPage && lastPage?.length !== 0
          ? lastPage[lastPage.length - 1]
          : null;
      if (lastPostComment?.id) {
        return {
          lastId: lastPostComment.id,
          direction: 'olderThan',
        };
      }
    },
    keepPreviousData: true,
    refetchOnMount: true,
    enabled: typeof communityId !== null && typeof postId !== null,
    refetchOnWindowFocus: false,
    refetchInterval: 30000,
  });
};
